import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditIcon from "@mui/icons-material/Edit";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useSelector } from "react-redux";
import moment from "moment";
import { showToast } from "services/utils/Status";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designShell,
} from "services/constants/designTokens";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import { CorrectionApplyPanel } from "pages/MyZone/MyZoneApply/CorrectionShared/CorrectionApplyPanel";
import {
  canonicalAttendanceCorrectionStatus,
  attendanceCorrectionStatusVisual,
} from "./AttendanceCorrectionStatusCards";
import { correctionMeta } from "./attendanceCorrectionApproveHelpers";

const detailAccent = {
  date: detailAccentPalette.indigo,
  type: detailAccentPalette.pink,
  earlyExit: detailAccentPalette.green,
  reason: detailAccentPalette.orange,
};

const punchHeadCell = {
  py: 0.6,
  px: 1,
  border: "none",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.03em",
  color: designColors.slate500,
};

const punchBodyCell = {
  py: 0.7,
  px: 1,
  border: "none",
  fontSize: 12.5,
  color: designColors.slate800,
};

// entries/original_entries only reliably carry {time, direction} (see
// attendanceCorrectionApproveHelpers) — original_entries adds its own `date`,
// requested entries don't, so requested rows fall back to the correction's
// own date instead of showing blank.
const formatEntryDate = (entry, fallbackDate) => {
  if (entry?.date) {
    const parsed = moment(entry.date, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
    if (parsed.isValid()) return parsed.format("DD-MM-YYYY");
  }
  if (entry?.punch_datetime) {
    const parsed = moment(entry.punch_datetime);
    if (parsed.isValid()) return parsed.format("DD-MM-YYYY");
  }
  return fallbackDate || "-";
};

const formatEntryTime = (time) => {
  const parsed = moment(time, ["HH:mm:ss", "HH:mm", "hh:mm A", "h:mm A"], true);
  return parsed.isValid() ? parsed.format("HH:mm") : time || "-";
};

// record.date is "YYYY-MM-DD"; show it the way every other date reads.
const formatArDate = (value) => {
  const parsed = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : value || "N/A";
};

// Same rules the form-based validation used to apply; returns "" when valid.
const validateRejectReason = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "Reject reason is required";
  if (trimmed.length < 3) return minimum3Characters;
  if (trimmed.length > 255) return "Maximum length is 255 characters";
  if (!atleastOneCharacterWithSpace.test(value)) return "Atleast one alphabet required";
  return "";
};

const directionStyle = (direction) =>
  String(direction || "").toUpperCase() === "IN"
    ? detailAccentPalette.green
    : detailAccentPalette.orange;

// One mini-table per punch list (current vs requested) so an approver can
// scan Date/Time/Direction for each side without cross-referencing two
// single-line strings — entries and original_entries are kept visually
// separate (icon + tint + own table) rather than merged into one table,
// since a missing punch means one side simply has fewer rows than the other.
const PunchEntriesTable = ({ title, icon: Icon, tint, entries = [], fallbackDate }) => (
  <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
    <Mui.Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
      <Icon sx={{ fontSize: 16, color: tint }} />
      <Mui.Typography sx={{ fontSize: 12.5, fontWeight: 700, color: designColors.slate700 }}>
        {title}
      </Mui.Typography>
    </Mui.Stack>
    {entries.length === 0 ? (
      <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate500, py: 0.5 }}>
        No punches
      </Mui.Typography>
    ) : (
      <Mui.Table size="small" sx={{ "& tr:last-child td": { pb: 0 } }}>
        <Mui.TableHead>
          <Mui.TableRow>
            <Mui.TableCell sx={punchHeadCell}>Date</Mui.TableCell>
            <Mui.TableCell sx={punchHeadCell}>Time</Mui.TableCell>
            <Mui.TableCell sx={punchHeadCell}>Direction</Mui.TableCell>
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {entries.map((entry, index) => {
            const visual = directionStyle(entry?.direction);
            return (
              <Mui.TableRow key={entry?.punch_id ?? index}>
                <Mui.TableCell sx={punchBodyCell}>{formatEntryDate(entry, fallbackDate)}</Mui.TableCell>
                <Mui.TableCell sx={punchBodyCell}>{formatEntryTime(entry?.time)}</Mui.TableCell>
                <Mui.TableCell sx={punchBodyCell}>
                  <Mui.Chip
                    size="small"
                    label={String(entry?.direction || "-").toUpperCase()}
                    sx={{
                      height: 20,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: visual.color,
                      backgroundColor: visual.bg,
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </Mui.TableCell>
              </Mui.TableRow>
            );
          })}
        </Mui.TableBody>
      </Mui.Table>
    )}
  </Mui.Box>
);

export const AttendanceCorrectionDetailPanel = ({
  open,
  onClose,
  record,
  checkAccess,
  onApprove,
  onReject,
  onCancel,
  onUpdated,
  actionLoading = false,
  expanded = false,
  onToggleExpand,
}) => {
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  // Plain state rather than react-hook-form: the old register()-in-useEffect
  // setup lost its rules on reset(), so after one empty Reject click the
  // "Reject reason is required" error could never clear.
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState("");
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  // No dedicated cancel-an-approved-attendance-correction endpoint exists
  // yet, same gap as Comp Off (see CompOffDetailPanel) — the parent's
  // onCancel reuses the same approve/reject endpoint with
  // status: "Cancelled" as a first attempt, needs verifying against the
  // real backend.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel used to be gated
  // by plain editor permission (checkAccess prop, from index.js's
  // check_access). Now requires real approver permission (either level
  // counts). Unlike every other Approve screen's detail panel, the no-access
  // case here used to fail silently (no showToast at all) — the toast calls
  // below are new, added for consistency with the rest.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Attendance Correction",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Attendance Correction",
    );

  useEffect(() => {
    setRejectDialogOpen(false);
    setRejectReason("");
    setRejectReasonError("");
    setEditPanelOpen(false);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  }, [record]);

  if (!record) return null;

  const status = canonicalAttendanceCorrectionStatus(record?.status);
  const isPending = status === "Pending";
  // Editing a Pending request only needs editor permission; approver flags
  // gate Approve/Reject/Cancel, not Edit.
  const canEditRequest = isPending && checkAccess;
  // Requested per user: allow cancelling an attendance correction request
  // even after it has been Approved (not just while Pending), same as Comp
  // Off.
  const canCancelApproved = status === "Approved";
  const employeeName = record?.employee_name || "N/A";

  const handleCloseAction = () => onClose?.();

  const handleEditDone = () => {
    setEditPanelOpen(false);
    onUpdated?.();
    onClose?.();
  };

  const handleApproveClick = () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this attendance correction request",
      );
      return;
    }
    onApprove?.(record);
  };

  const submitReject = () => {
    const error = validateRejectReason(rejectReason);
    if (error) {
      setRejectReasonError(error);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this attendance correction request",
      );
      return;
    }
    onReject?.(record, rejectReason.trim());
    setRejectDialogOpen(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectReason("");
    setRejectReasonError("");
  };

  const submitCancel = () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this attendance correction request",
      );
      return;
    }
    onCancel?.(record, trimmed);
    setCancelDialogOpen(false);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  // Reject/Approve/Cancel below used to always render, only disabled (via
  // AccessGatedAction) when hasApproverAccess was false — leaving a
  // bordered footer strip with unusable buttons for an editor-only viewer.
  // Each branch now resolves to real content or a genuine `null` (same fix
  // as OverTime/DetailPanel.js) so GlobalSidePanel's own footer border only
  // draws when there's actually something to show.
  const footerContent = isPending ? (
    hasApproverAccess ? (
      <Mui.Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon />}
          disabled={actionLoading}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelDangerButton,
            color: "#B42318",
            borderColor: "#F5C2C7",
            backgroundColor: "#FEF3F2",
            "&:hover": { borderColor: "#E59CA5", backgroundColor: "#FEE4E2" },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon />}
          disabled={actionLoading}
          loading={actionLoading}
          onClick={handleApproveClick}
        >
          Approve
        </PrimaryButton>
      </Mui.Box>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5}>
        <Mui.Typography sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}>
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Attendance Correction
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : (
    <Mui.Typography sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}>
      This request has already been {status?.toLowerCase() || "processed"}.
    </Mui.Typography>
  );

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Attendance Correction Request"
      onCancel={closeRejectDialog}
      onConfirm={submitReject}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={actionLoading}
      danger
      reasonValue={rejectReason}
      onReasonChange={(value) => {
        setRejectReason(value);
        // Re-validate as they type once an error is showing, so it clears
        // as soon as the reason becomes valid.
        if (rejectReasonError) setRejectReasonError(validateRejectReason(value));
      }}
      reasonError={rejectReasonError}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this attendance correction request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Attendance Correction Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={actionLoading}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved attendance correction"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This attendance correction request has already been approved. Enter
        a reason to cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Attendance Correction"
        subtitle="Request details and status"
        headerActions={
          canEditRequest ? (
            <Mui.Tooltip title="Edit attendance correction request" arrow slotProps={tooltipStyleProps}>
              <Mui.IconButton
                aria-label="Edit attendance correction request"
                size="small"
                onClick={() => setEditPanelOpen(true)}
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <EditIcon fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
          ) : null
        }
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Attendance Correction", onClick: handleCloseAction },
          employeeName || "Attendance Correction Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: record?.image_url,
              employee_id: record?.employee_id,
              user_name: employeeName,
              department_name: record?.department,
              designation: record?.designation,
            }}
            status={status}
            colorCode={record?.color_code || attendanceCorrectionStatusVisual(status).accent}
          />

          <Mui.Box sx={detailCardSx}>
            <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
              Correction Details
            </Mui.Typography>
            <RequestDetailField
              icon={CalendarMonthIcon}
              label="AR Date"
              value={formatArDate(record?.date)}
              accent={detailAccent.date}
            />
            <RequestDetailField
              icon={EditCalendarOutlinedIcon}
              label="Correction Type"
              value={correctionMeta(record?.correction_type).label}
              accent={detailAccent.type}
            />
            <RequestDetailField
              icon={CheckCircleOutlineIcon}
              label="Early Exit"
              value={record?.early_exit ? "Yes" : "No"}
              accent={detailAccent.earlyExit}
              isLast
            />

            <Mui.Divider sx={{ my: 1.6, borderColor: designColors.slate100 }} />
            <Mui.Typography
              sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.03em", color: designColors.slate500, mb: 1.25 }}
            >
              PUNCH DETAILS
            </Mui.Typography>
            <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              <PunchEntriesTable
                title="Current Punches"
                icon={HistoryOutlinedIcon}
                tint={designColors.slate500}
                entries={record?.original_entries}
                fallbackDate={record?.date ? formatArDate(record.date) : "-"}
              />
              <PunchEntriesTable
                title="Requested Punches"
                icon={AccessTimeIcon}
                tint={detailAccentPalette.green.color}
                entries={record?.entries}
                fallbackDate={record?.date ? formatArDate(record.date) : "-"}
              />
            </Mui.Stack>

            <Mui.Divider sx={{ my: 1.6, borderColor: designColors.slate100 }} />
            <RequestDetailField
              icon={DescriptionOutlinedIcon}
              label="Reason"
              value={record?.reason || "N/A"}
              accent={detailAccent.reason}
              multiline
              isLast
            />
          </Mui.Box>

          <RequestStatusTimeline endDate={record?.date} data={record} hidePendingStep />
          <ApprovalEditAuditTrail data={record} />
        </Mui.Box>
      </GlobalSidePanel>
      {editPanelOpen ? (
        <CorrectionApplyPanel
          open={editPanelOpen}
          onClose={() => setEditPanelOpen(false)}
          onCreate={handleEditDone}
          title="Edit Attendance Correction"
          subtitle="Correct missing, incorrect or wrong punch entries for this day."
          editRecord={record}
          // Without this the panel fetched/edited the logged-in approver's
          // own attendance instead of the employee the request is actually
          // for, since it otherwise always falls back to `userDetails`.
          targetUser={{
            id: record?.user_id,
            employee_id: record?.employee_id,
            user_name: record?.employee_name,
            department_name: record?.department,
            designation: record?.designation,
            image_url: record?.image_url,
          }}
        />
      ) : null}
      {rejectDialog}
      {cancelDialog}
    </>
  );
};

export default AttendanceCorrectionDetailPanel;
